import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { TASK_TYPES, taskMeta, customTypeMeta, currency, INVOICE_STATUS } from "@/lib/portalConstants";
import { format, parseISO } from "date-fns";
import { printCareProfile } from "@/lib/printTemplates";
import {
  ArrowLeft, Loader2, Phone, Mail, MapPin, ClipboardList, AlertTriangle,
  FileText, KeyRound, Repeat, CalendarDays, HeartHandshake, Plus, Pencil, MessageCircle, ChevronDown, Printer,
} from "lucide-react";
import { TaskDialog } from "@/components/portal/TaskDialog";
import { InvoiceDialog } from "@/components/portal/InvoiceDialog";
import { CareLogDialog } from "@/components/portal/CareLogDialog";
import { IncidentDialog } from "@/components/portal/IncidentDialog";
import { NotesThread } from "@/components/portal/NotesThread";

const Section = ({ icon: Icon, title, count, action, children, testid }) => (
  <section data-testid={testid} className="bg-white rounded-2xl border border-charcoal/5 soft-shadow">
    <header className="flex items-center gap-2.5 px-5 sm:px-6 py-4 border-b border-charcoal/5">
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-sage/15 text-sage-700">
        <Icon className="h-4 w-4" />
      </span>
      <h2 className="font-serif text-xl text-charcoal">{title}</h2>
      {count != null && <span className="text-xs text-charcoal-soft">({count})</span>}
      {action && <div className="ml-auto">{action}</div>}
    </header>
    <div className="p-5 sm:p-6">{children}</div>
  </section>
);

const AddBtn = ({ onClick, testid, children }) => (
  <button onClick={onClick} data-testid={testid} className="inline-flex items-center gap-1.5 text-xs font-medium text-sage-700 hover:text-sage-dark rounded-full border border-sage/30 px-3 py-1.5 transition-colors">
    <Plus className="h-3.5 w-3.5" /> {children}
  </button>
);

const Empty = ({ children }) => <p className="text-sm text-charcoal/40 py-2">{children}</p>;

export default function ClientDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [customTypes, setCustomTypes] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [taskDlg, setTaskDlg] = useState({ open: false, edit: null });
  const [invDlg, setInvDlg] = useState({ open: false, edit: null });
  const [logDlg, setLogDlg] = useState({ open: false, edit: null });
  const [incOpen, setIncOpen] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState({});
  const [logLimit, setLogLimit] = useState(10);

  const load = useCallback(async () => {
    try {
      const [o, c, tt, s] = await Promise.all([
        api.get(`/portal/clients/${id}/overview`),
        api.get("/portal/clients"),
        api.get("/portal/task-types"),
        api.get("/portal/service-items"),
      ]);
      setData(o.data);
      setClients(c.data);
      setCustomTypes(tt.data.map(customTypeMeta));
      setServices(s.data);
    } catch {
      setNotFound(true);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshTypes = async () => {
    const tt = await api.get("/portal/task-types");
    setCustomTypes(tt.data.map(customTypeMeta));
  };

  if (notFound)
    return (
      <div className="text-center py-24" data-testid="client-not-found">
        <p className="text-charcoal-soft">Client not found.</p>
        <Link to="/portal/clients" className="text-sage-700 hover:underline text-sm">Back to clients</Link>
      </div>
    );
  if (!data)
    return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-sage" /></div>;

  const { client: c, tasks, care_logs, incidents, invoices, family } = data;
  const allTypes = [...TASK_TYPES, ...customTypes];
  const metaOf = (type) => allTypes.find((x) => x.value === type) || taskMeta("other");

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="client-detail-page">
      <Link to="/portal/clients" data-testid="back-to-clients" className="inline-flex items-center gap-2 text-sm text-charcoal-soft hover:text-charcoal transition-colors">
        <ArrowLeft className="h-4 w-4" /> All clients
      </Link>

      {/* Profile */}
      <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-charcoal" data-testid="client-detail-name">{c.name}</h1>
            {(c.approx_age || c.cognitive || c.mobility || c.hourly_rate > 0) && (
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid="client-descriptors">
                {c.approx_age && <span className="text-xs px-2.5 py-1 rounded-full bg-sage/15 text-sage-700 font-medium">Age ~{c.approx_age}</span>}
                {c.hourly_rate > 0 && <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium" data-testid="client-rate-chip">${c.hourly_rate}/hr</span>}
                {c.cognitive && <span className="text-xs px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 font-medium">{c.cognitive}</span>}
                {c.mobility && <span className="text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">{c.mobility}</span>}
              </div>
            )}
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full ${c.active !== false ? "bg-emerald-100 text-emerald-700" : "bg-charcoal/10 text-charcoal-soft"}`}>
            {c.active !== false ? "Active" : "Inactive"}
          </span>
        </div>

        {c.care_needs?.length > 0 && (
          <div className="mt-5" data-testid="client-care-needs">
            <p className="text-xs font-medium text-charcoal-soft uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <HeartHandshake className="h-3.5 w-3.5" /> Care needs
            </p>
            <div className="flex flex-wrap gap-1.5">
              {c.care_needs.map((n) => (
                <span key={n} className="text-xs px-2.5 py-1 rounded-full bg-terracotta/10 text-terracotta">{n}</span>
              ))}
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 mt-6 text-sm">
          {c.contact_name && (
            <p className="text-charcoal">Authorized contact: <span className="text-charcoal-soft">{c.contact_name}{c.contact_relationship && ` (${c.contact_relationship})`}</span></p>
          )}
          {c.contact_phone && (
            <a href={`tel:${c.contact_phone}`} className="flex items-center gap-2 text-charcoal-soft hover:text-charcoal"><Phone className="h-4 w-4 text-sage" /> {c.contact_phone}</a>
          )}
          {c.contact_email && (
            <a href={`mailto:${c.contact_email}`} className="flex items-center gap-2 text-charcoal-soft hover:text-charcoal"><Mail className="h-4 w-4 text-sage" /> {c.contact_email}</a>
          )}
          {c.address && <p className="flex items-center gap-2 text-charcoal-soft"><MapPin className="h-4 w-4 text-sage" /> {c.address}</p>}
        </div>
        {c.notes && <p className="text-sm text-charcoal-soft mt-4 italic border-l-2 border-sage/30 pl-3">{c.notes}</p>}

        {/* Quick actions — everything for this client, right here */}
        <div className="flex flex-wrap gap-2 mt-6 pt-5 border-t border-charcoal/5" data-testid="client-quick-actions">
          <AddBtn onClick={() => setTaskDlg({ open: true, edit: null })} testid="quick-add-visit">Schedule visit</AddBtn>
          <AddBtn onClick={() => setLogDlg({ open: true, edit: null })} testid="quick-add-log">New care log</AddBtn>
          <AddBtn onClick={() => setInvDlg({ open: true, edit: null })} testid="quick-add-invoice">New invoice</AddBtn>
          <button onClick={() => setIncOpen(true)} data-testid="quick-add-incident" className="inline-flex items-center gap-1.5 text-xs font-medium text-terracotta hover:opacity-80 rounded-full border border-terracotta/30 px-3 py-1.5 transition-opacity">
            <Plus className="h-3.5 w-3.5" /> Log incident
          </button>
          <button onClick={() => printCareProfile(c, tasks, care_logs, incidents)} data-testid="print-profile-button" title="Print a care profile for audits or family meetings" className="inline-flex items-center gap-1.5 text-xs font-medium text-charcoal-soft hover:text-charcoal rounded-full border border-charcoal/15 px-3 py-1.5 transition-colors ml-auto">
            <Printer className="h-3.5 w-3.5" /> Print profile
          </button>
        </div>

        <div className="mt-5 pt-4 border-t border-charcoal/5" data-testid="client-family-logins">
          <p className="text-xs font-medium text-charcoal-soft uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
            <KeyRound className="h-3.5 w-3.5" /> Family logins
          </p>
          {family.length === 0 ? (
            <p className="text-xs text-charcoal/40">None yet — manage them from the Clients page.</p>
          ) : (
            <ul className="text-sm text-charcoal space-y-1">
              {family.map((u) => <li key={u.id}>{u.name} <span className="text-charcoal-soft">· {u.email}</span></li>)}
            </ul>
          )}
        </div>
      </div>

      {/* Care logs — collapsible so years of records stay tidy */}
      <Section icon={ClipboardList} title="Care log" count={care_logs.length} testid="section-care-logs" action={<AddBtn onClick={() => setLogDlg({ open: true, edit: null })} testid="section-add-log">Add</AddBtn>}>
        {care_logs.length === 0 ? <Empty>No care logs yet — add the first one right here.</Empty> : (
          <div>
            <div className="divide-y divide-charcoal/5">
              {care_logs.slice(0, logLimit).map((log) => {
                const open = !!expandedLogs[log.id];
                return (
                  <article key={log.id} data-testid={`detail-log-${log.id}`}>
                    <button
                      onClick={() => setExpandedLogs((s) => ({ ...s, [log.id]: !open }))}
                      data-testid={`log-toggle-${log.id}`}
                      className="w-full flex items-center gap-3 py-3 text-left group"
                    >
                      <ChevronDown className={`h-4 w-4 text-charcoal/35 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                      <span className="font-medium text-charcoal shrink-0">{format(parseISO(log.date), "EEE, MMM d, yyyy")}</span>
                      {log.hours > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 shrink-0">{log.hours} hrs</span>}
                      {log.wellbeing && <span className="text-[11px] px-2 py-0.5 rounded-full bg-sage/15 text-sage-700 shrink-0 hidden sm:inline">{log.wellbeing}</span>}
                      {log.summary && !open && <span className="text-sm text-charcoal/40 truncate flex-1 hidden md:block">{log.summary}</span>}
                      <span className="flex-1" />
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setLogDlg({ open: true, edit: log }); }}
                        onKeyDown={(e) => e.key === "Enter" && (e.stopPropagation(), setLogDlg({ open: true, edit: log }))}
                        data-testid={`detail-edit-log-${log.id}`}
                        className="p-1.5 text-charcoal/30 hover:text-charcoal sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </span>
                    </button>
                    {open && (
                      <div className="pb-4 pl-7" data-testid={`log-detail-${log.id}`}>
                        {log.wellbeing && <span className="text-[11px] px-2 py-0.5 rounded-full bg-sage/15 text-sage-700 sm:hidden">{log.wellbeing}</span>}
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {(log.personal_care || []).map((x) => <span key={x} className="text-[11px] px-2 py-0.5 rounded-full bg-terracotta/10 text-terracotta">{x}</span>)}
                          {(log.assisted_with || []).map((x) => <span key={x} className="text-[11px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">{x}</span>)}
                          {(log.housekeeping || []).map((x) => <span key={x} className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{x}</span>)}
                        </div>
                        {log.meals_hydration && <p className="text-xs text-charcoal-soft mt-2">Meals & hydration: {log.meals_hydration}</p>}
                        {log.summary && <p className="text-sm text-charcoal mt-2">{log.summary}</p>}
                        {log.notes && <p className="text-xs text-charcoal/50 italic mt-1">{log.notes}</p>}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
            {care_logs.length > logLimit && (
              <button
                onClick={() => setLogLimit((n) => n + 25)}
                data-testid="show-older-logs"
                className="w-full mt-2 py-2.5 text-sm text-sage-700 hover:text-sage-dark font-medium rounded-xl hover:bg-sand transition-colors"
              >
                Show older entries ({care_logs.length - logLimit} more)
              </button>
            )}
          </div>
        )}
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 items-start">
        {/* Schedule */}
        <Section icon={CalendarDays} title="Scheduled visits" count={tasks.length} testid="section-schedule" action={<AddBtn onClick={() => setTaskDlg({ open: true, edit: null })} testid="section-add-visit">Add</AddBtn>}>
          {tasks.length === 0 ? <Empty>No visits scheduled — add one right here.</Empty> : (
            <ul className="space-y-2.5">
              {tasks.map((t) => {
                const meta = metaOf(t.type);
                return (
                  <li key={t.id} data-testid={`detail-task-${t.id}`} className="flex items-center gap-3 text-sm group">
                    <span className={`h-2 w-2 rounded-full shrink-0 ${meta.dot}`} />
                    <span className="text-charcoal-soft shrink-0 w-28">{format(parseISO(t.date), "EEE, MMM d")}</span>
                    {t.start_time && <span className="text-charcoal-soft shrink-0">{t.start_time}</span>}
                    <span className={`truncate flex-1 ${t.done ? "line-through text-charcoal/40" : "text-charcoal"}`}>
                      {t.title}
                      {t.series_id && <Repeat className="inline h-3 w-3 text-sage ml-1 align-[-1px]" />}
                    </span>
                    <button onClick={() => setTaskDlg({ open: true, edit: t })} data-testid={`detail-edit-task-${t.id}`} className="p-1.5 text-charcoal/30 hover:text-charcoal sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0"><Pencil className="h-3.5 w-3.5" /></button>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        {/* Invoices */}
        <Section icon={FileText} title="Invoices" count={invoices.length} testid="section-invoices" action={<AddBtn onClick={() => setInvDlg({ open: true, edit: null })} testid="section-add-invoice">Add</AddBtn>}>
          {invoices.length === 0 ? <Empty>No invoices yet — create one right here.</Empty> : (
            <ul className="space-y-2.5">
              {invoices.map((inv) => (
                <li key={inv.id} data-testid={`detail-invoice-${inv.id}`} className="flex items-center justify-between gap-3 text-sm group">
                  <Link to="/portal/invoices" className="text-charcoal hover:text-sage-700 font-medium">{inv.invoice_number}</Link>
                  <span className="text-charcoal-soft hidden sm:inline">{inv.issue_date}</span>
                  <span className="text-charcoal font-medium">{currency(inv.total)}</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${INVOICE_STATUS[inv.status]?.color || ""}`}>{INVOICE_STATUS[inv.status]?.label || inv.status}</span>
                  {inv.status !== "paid" && (
                    <button onClick={() => setInvDlg({ open: true, edit: inv })} data-testid={`detail-edit-invoice-${inv.id}`} className="p-1.5 text-charcoal/30 hover:text-charcoal sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0"><Pencil className="h-3.5 w-3.5" /></button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {/* Client notes — Nicole's notebook, optionally shared with family */}
      <Section icon={MessageCircle} title="Notes" testid="section-notes">

        <NotesThread clientId={c.id} mode="admin" />
      </Section>

      {/* Incidents */}
      <Section icon={AlertTriangle} title="Incident reports" count={incidents.length} testid="section-incidents" action={<AddBtn onClick={() => setIncOpen(true)} testid="section-add-incident">Add</AddBtn>}>
        {incidents.length === 0 ? <Empty>No incidents on record. May it stay that way.</Empty> : (
          <div className="space-y-4">
            {incidents.map((i) => (
              <article key={i.id} data-testid={`detail-incident-${i.id}`} className="border-b border-charcoal/5 last:border-0 pb-4 last:pb-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-charcoal">{i.occurred_at?.replace("T", " ")}</p>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${i.resolved ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                    {i.resolved ? "Resolved" : "Open"}
                  </span>
                </div>
                <p className="text-sm text-charcoal mt-1.5">{i.what_happened}</p>
                {i.action_taken && <p className="text-xs text-charcoal-soft mt-1">Action taken: {i.action_taken}</p>}
                {i.outcome && <p className="text-xs text-charcoal-soft mt-0.5">Outcome: {i.outcome}</p>}
              </article>
            ))}
          </div>
        )}
      </Section>

      <TaskDialog
        open={taskDlg.open}
        onOpenChange={(o) => setTaskDlg((s) => ({ ...s, open: o }))}
        clients={clients}
        allTypes={allTypes}
        customTypeCount={customTypes.length}
        editTask={taskDlg.edit}
        initialClientId={c.id}
        onTypesChanged={refreshTypes}
        onSaved={load}
      />
      <InvoiceDialog
        open={invDlg.open}
        onOpenChange={(o) => setInvDlg((s) => ({ ...s, open: o }))}
        clients={clients}
        services={services}
        editInvoice={invDlg.edit}
        initialClientId={c.id}
        onSaved={load}
      />
      <CareLogDialog
        open={logDlg.open}
        onOpenChange={(o) => setLogDlg((s) => ({ ...s, open: o }))}
        clients={clients}
        editLog={logDlg.edit}
        initialClientId={c.id}
        onSaved={load}
      />
      <IncidentDialog open={incOpen} onOpenChange={setIncOpen} clients={clients} initialClientId={c.id} onSaved={load} />
    </div>
  );
}
